/**
 * useContalinkInvoices - Gestiona facturas pendientes de registrar en Contalink
 */

import { createHttpClient } from '@adapters/HttpClient';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services/ApiClient';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoicesApiAdapter } from '../adapters/InvoicesApiAdapter';
import { ContalinkPendingInvoicesModalRef } from '../components/modals/ContalinkPendingInvoicesModal';
import { Invoice } from '../models/Invoices';

export const useContalinkInvoices = () => {
  const { t } = useTranslation('invoicesModule');
  const { toast } = useToast();
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  const modalRef = useRef<ContalinkPendingInvoicesModalRef>(null);

  const showPendingInvoices = useCallback(
    async (onSelectInvoice: (invoice: Invoice) => void) => {
      setIsLoadingPending(true);

      try {
        const adapter = new InvoicesApiAdapter(createHttpClient(apiClientWH));
        const result = await adapter.getPendingContalinkInvoices();
        const pendingInvoices = result.data || [];

        if (pendingInvoices.length === 0) {
          toast({
            title: t('toast.contalink.not_found.title', 'Sin facturas pendientes'),
            description: t(
              'toast.contalink.not_found.description',
              'No hay facturas pendientes de registrar en Contalink',
            ),
            variant: 'default',
          });
          return;
        }

        modalRef.current?.open(pendingInvoices, onSelectInvoice);
      } catch (error: any) {
        toast({
          title: t('toast.contalink.error.title', 'Error al obtener facturas'),
          description:
            error?.message || t('toast.contalink.error.fetch', 'Error al obtener facturas pendientes de Contalink'),
          variant: 'destructive',
        });
      } finally {
        setIsLoadingPending(false);
      }
    },
    [t, toast],
  );

  return {
    showPendingInvoices,
    isLoadingPending,
    modalRef,
  };
};
